import { watch, nextTick, onMounted, getCurrentInstance } from 'vue'
import { vmHasRouter } from '../utils/vm.js'

export const useModelToggleProps = {
  modelValue: {
    type: Boolean,
    default: null
  },
  'onUpdate:modelValue': [Function, Array]
}

export const useModelToggleEmits = ['beforeShow', 'show', 'beforeHide', 'hide']

export default function ({
  showing,
  canShow,
  hideOnRouteChange,
  handleShow,
  handleHide,
  processOnMount
}) {
  const vm = getCurrentInstance()
  const { props, emit, proxy } = vm
  let payload

  function toggle(evt) {
    showing.value ? hide(evt) : show(evt)
  }

  function show(evt) {
    if (shouldNotToggle(evt)) return

    const hasListener = props['onUpdate:modelValue'] !== undefined

    if (hasListener) {
      updateModelValue(true, evt)
    } else {
      processShow(evt)
    }
  }

  function hide(evt) {
    if (props.disable) return

    const hasListener = props['onUpdate:modelValue'] !== undefined

    if (hasListener) {
      updateModelValue(false, evt)
    } else {
      processHide(evt)
    }
  }

  function shouldNotToggle(evt) {
    return (
      props.disable || (evt && evt.qAnchorHandled) || (canShow && !canShow(evt))
    )
  }

  function updateModelValue(value, evt) {
    emit('update:modelValue', value)
    payload = evt
    nextTick(() => {
      if (payload === evt) payload = undefined
    })
  }

  function processShow(evt) {
    if (showing.value) return

    showing.value = true
    emit('beforeShow', evt)
    handleShow ? handleShow(evt) : emit('show', evt)
  }

  function processHide(evt) {
    if (!showing.value) return

    showing.value = false
    emit('beforeHide', evt)
    handleHide ? handleHide(evt) : emit('hide', evt)
  }

  function processModelChange(val) {
    if (props.disable && val) {
      if (props['onUpdate:modelValue'] !== undefined) {
        emit('update:modelValue', false)
      }
    } else if (val !== showing.value) {
      const action = val ? processShow : processHide
      action(payload)
    }
  }

  watch(() => props.modelValue, processModelChange)

  if (hideOnRouteChange && vmHasRouter(vm)) {
    watch(
      () => proxy.$route.fullPath,
      () => {
        if (hideOnRouteChange.value && showing.value) {
          hide()
        }
      }
    )
  }

  processOnMount &&
    onMounted(() => {
      processModelChange(props.modelValue)
    })

  Object.assign(proxy, { show, hide, toggle })

  return { show, hide, toggle }
}
